import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { apiRequest } from './api';
import { isNativeApp } from './platform';

const Settings = registerPlugin('NativePushSettings');
const subscribers = new Set();
let state = { phase: 'idle', message: 'Zet meldingen aan om native push te testen.' };
let currentToken = null;
let pendingRegistration = null;
let generation = 0;
let registrationTask = null;
const publish = (next) => {
  state = { ...state, ...next };
  subscribers.forEach((callback) => callback(state));
};
export const subscribeNativePush = (callback) => {
  subscribers.add(callback);
  callback(state);
  return () => subscribers.delete(callback);
};
export const isAndroidPush = () => isNativeApp() && Capacitor.getPlatform() === 'android';
export const openPushSettings = () => Settings.open();

const bindToken = async (token, expectedGeneration) => {
  if (expectedGeneration !== generation) return;
  currentToken = token;
  await apiRequest('/api/push/devices', { method: 'POST', body: JSON.stringify({ token }) });
  if (expectedGeneration !== generation) return;
  currentToken = token;
  publish({ phase: 'ready', message: 'Dit toestel is aangemeld voor native push.' });
};

export const enableNativePush = () => {
  if (registrationTask) return registrationTask;
  registrationTask = (async () => {
    const expectedGeneration = generation;
    try {
      publish({ phase: 'loading', message: 'Meldingen worden ingesteld…' });
      const status = await Settings.status();
      if (!status.configured) {
        publish({ phase: 'unconfigured', message: 'De Firebase-koppeling ontbreekt in deze appversie. Installeer de versie met Firebase om push te testen.' });
        return false;
      }
      let permission = await PushNotifications.checkPermissions();
      if (permission.receive !== 'granted') permission = await PushNotifications.requestPermissions();
      if (permission.receive !== 'granted' || !(await Settings.status()).enabled) {
        publish({ phase: 'denied', message: 'Meldingen staan uit. Zet ze aan in de Android-instellingen.' });
        return false;
      }
      await PushNotifications.createChannel({ id: 'cuplio_updates', name: 'Cuplio meldingen', description: 'Updates en testmeldingen van Cuplio', importance: 4, visibility: 0, sound: 'default' });
      // Listener is installed by NativePushBridge before this call.
      const token = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          pendingRegistration = null;
          reject(new Error('Aanmelden duurt te lang. Controleer je verbinding en probeer opnieuw.'));
        }, 15000);
        pendingRegistration = {
          resolve: (value) => { clearTimeout(timeout); pendingRegistration = null; resolve(value); },
          reject: (error) => { clearTimeout(timeout); pendingRegistration = null; reject(error); },
        };
        PushNotifications.register().catch((error) => pendingRegistration?.reject(error));
      });
      await bindToken(token, expectedGeneration);
      return expectedGeneration === generation;
    } catch (error) {
      if (expectedGeneration === generation) publish({ phase: 'error', message: error.message || 'Push aanmelden is mislukt. Probeer opnieuw.' });
      return false;
    }
  })().finally(() => { registrationTask = null; });
  return registrationTask;
};

export const testNativePush = async () => {
  if (!(await enableNativePush())) return;
  publish({ phase: 'testing', message: 'Zet de app nu op de achtergrond. De server verstuurt de testmelding over 8 seconden.' });
  try {
    await apiRequest('/api/push/test', { method: 'POST', body: JSON.stringify({ token: currentToken }) });
    // A received event may already have confirmed delivery while this request was active.
    if (state.phase === 'testing') publish({ phase: 'ready', message: 'Firebase heeft de melding geaccepteerd. Controleer je Android-meldingen; aflevering kan even duren.' });
  } catch (error) {
    publish({ phase: 'error', message: error.message || 'De testmelding kon niet worden verstuurd.' });
  }
};

export const deactivateNativePush = async () => {
  if (!isAndroidPush()) return;
  generation += 1;
  pendingRegistration?.reject(new Error('Pushregistratie is gestopt.'));
  await registrationTask;
  const token = currentToken;
  currentToken = null;
  try {
    if (token) await apiRequest('/api/push/devices', { method: 'DELETE', body: JSON.stringify({ token }) });
  } finally {
    // Delete the Firebase token even when our backend cannot be reached.
    const status = await Settings.status();
    if (status.configured) await Settings.invalidate();
    await PushNotifications.removeAllDeliveredNotifications();
    publish({ phase: 'idle', message: 'Zet meldingen aan om native push te testen.' });
  }
};

export const getPushDestination = (notification) => {
  const data = notification?.data;
  if (data?.kind === 'test' && data.path === '/profile') return { path: '/profile' };
  const expectedPath = { match_reminder: '/input', settlement_reminder: '/settlements' }[data?.kind];
  const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!expectedPath || data.path !== expectedPath || !uuid.test(data.group_id) || !uuid.test(data.match_id)) return null;
  return { path: `${expectedPath}?match=${data.match_id}`, groupId: data.group_id, matchId: data.match_id };
};

export const attachNativePush = async (onTap) => {
  const handles = [];
  try {
    handles.push(await PushNotifications.addListener('registration', ({ value }) => {
      if (pendingRegistration) pendingRegistration.resolve(value);
      else if (currentToken) bindToken(value, generation).catch(() => publish({ phase: 'error', message: 'Toestelregistratie vernieuwen is mislukt. Meld push opnieuw aan.' }));
    }));
    handles.push(await PushNotifications.addListener('registrationError', () => {
      pendingRegistration?.reject(new Error('Firebase-aanmelding is mislukt. Controleer je verbinding en de Firebase-configuratie.'));
    }));
    handles.push(await PushNotifications.addListener('pushNotificationReceived', () => {
      publish({ phase: 'ready', message: 'Native push ontvangen op dit toestel.' });
    }));
    handles.push(await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      // Accept only known in-app destinations, never arbitrary notification URLs.
      const destination = getPushDestination(notification);
      if (destination) onTap(destination.path, destination);
    }));
    const status = await Settings.status();
    if (!status.configured) publish({ phase: 'unconfigured', message: 'De Firebase-koppeling ontbreekt in deze appversie. Installeer de versie met Firebase om push te testen.' });
    else {
      const permission = await PushNotifications.checkPermissions();
      if (permission.receive === 'granted' && status.enabled) await enableNativePush();
    }
    return () => { handles.forEach((handle) => handle.remove()); };
  } catch (error) {
    handles.forEach((handle) => handle.remove());
    throw error;
  }
};
